import { useState } from "react";
import { Button } from "@/components/ui/button";
import { PenSquare } from "lucide-react";
import { SidebarMenuItem, SidebarMenuButton } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { CategoryEditPopover } from "./CategoryEditPopover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface CategoryItemProps {
  category: { 
    name: string;
    iconSrc?: string;
  };
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (categoryName: string, newName: string) => void;
  onDelete: (categoryName: string) => void;
}

export const CategoryItem = ({
  category,
  isSelected,
  onSelect,
  onUpdate,
  onDelete,
}: CategoryItemProps) => {
  const [showEdit, setShowEdit] = useState(false);

  if (!category) {
    console.warn('Invalid category prop in CategoryItem');
    return null;
  }

  const getIconForCategory = (categoryName: string) => {
    if (category.iconSrc) {
      return {
        src: category.iconSrc,
        alt: `${categoryName} icon`
      };
    }

    const iconMap: Record<string, { src: string; alt: string }> = {
      "Mind & Meditation": {
        src: "/lovable-uploads/f330a87b-511b-4740-a858-52f649e4d1f5.png",
        alt: "Lotus flower icon"
      },
      "Nature": {
        src: "/lovable-uploads/3e3bdc01-fb6a-45a3-9c04-3b215b16e2a3.png",
        alt: "Nature icon"
      },
      "Arts & Music": {
        src: "/lovable-uploads/aa9b79a2-7b66-4efc-9c94-26e9c4ae085f.png",
        alt: "Arts icon"
      },
      "Learning": {
        src: "/lovable-uploads/372f75f3-2e89-43c8-bde7-1d680a38d087.png",
        alt: "Learning icon"
      },
      "Creativity": {
        src: "/lovable-uploads/46d89a14-e095-410b-bae1-97420f0505f5.png",
        alt: "Creativity icon"
      },
      "Health": {
        src: "/lovable-uploads/d8e2f0f1-9eca-487f-95a8-99ceacbb4333.png",
        alt: "Health icon"
      },
      "Fitness": {
        src: "/lovable-uploads/c85c587f-41ad-461e-b24e-c6d45372fc32.png",
        alt: "Fitness icon"
      },
      "Travel": {
        src: "/lovable-uploads/6073096a-24b9-4392-b0e4-1165d19a5c40.png",
        alt: "Travel icon"
      },
      "Sports": {
        src: "/lovable-uploads/03d924d0-254d-48e0-8f7c-7046387ed882.png",
        alt: "Sports icon"
      },
      "Lifestyle": {
        src: "/lovable-uploads/96d30dee-a1c3-4ae8-bc1e-5072e7bb02d6.png",
        alt: "Lifestyle icon"
      }
    };

    return iconMap[categoryName];
  };

  const renderCategoryContent = () => {
    const icon = getIconForCategory(category.name);
    
    if (icon) {
      return (
        <>
          <img 
            src={icon.src}
            alt={icon.alt}
            className="w-4 h-4 mr-2"
          />
          <span>{category.name}</span>
        </>
      );
    }
    return <span>{category.name}</span>;
  };

  return (
    <div
      className={cn(
        "w-full rounded-md",
        isSelected ? "bg-primary/10" : "",
        "hover:bg-primary/5 group"
      )}
      onMouseEnter={() => setShowEdit(true)}
      onMouseLeave={() => setShowEdit(false)}
    >
      <div className="flex items-center justify-between w-full px-2">
        <SidebarMenuButton
          onClick={onSelect}
          className={cn(
            "w-full transition-all duration-200 rounded-full px-3",
            isSelected ? "text-primary font-medium" : "text-gray-800",
            "hover:text-primary"
          )}
        >
          <div className="flex items-center">
            {renderCategoryContent()}
          </div>
        </SidebarMenuButton>
        {showEdit && (
          <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
            <CategoryEditPopover
              category={category}
              onUpdate={onUpdate}
              onDelete={onDelete}
              trigger={
                <Button variant="ghost" size="icon" className="h-6 w-6 ml-1">
                  <PenSquare className="h-3 w-3" />
                </Button>
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};
